import { inject, Injectable } from '@angular/core';
import { QueryType } from 'enums/QueryType';
import { DynamicQueryModel } from 'models/generated/Models.DynamicQuery';
import { SelectListItemModel } from 'models/SelectList';
import { ProductService } from 'services/product.service';
import { ProductPlanService } from 'services/productplan.service';
import { QueryService } from 'services/query.service';
import { ReasonService } from 'services/reason.service';
import { TaskService } from 'services/task.service';
import { TaskflowTemplateService } from 'services/taskflow-template.service';
import { UserService } from 'services/user.service';

/**
 * The options for the dropdowns of a list of query inputs. Each list is requested the first time an input needs it and shared by
 * all the inputs, so inputs of the same type don't request it again. Only provided by QueryInputsComponent, so it lives as long as
 * the list of inputs and the options are requested again the next time one is shown.
 */
@Injectable()
export class QueryInputOptionsService {

	private readonly queryService = inject(QueryService);
	private readonly userService = inject(UserService);
	private readonly taskService = inject(TaskService);
	private readonly reasonService = inject(ReasonService);
	private readonly productPlanService = inject(ProductPlanService);
	private readonly productService = inject(ProductService);
	private readonly taskflowTemplateService = inject(TaskflowTemplateService);

	private teamList?: Promise<SelectListItemModel[]>;
	private taskAreaList?: Promise<SelectListItemModel[]>;
	private reasonList?: Promise<SelectListItemModel[]>;
	private savedQueryList?: Promise<{ queryType: QueryType, item: SelectListItemModel }[]>;
	private productPlanTagList?: Promise<SelectListItemModel[]>;
	private productTagList?: Promise<SelectListItemModel[]>;
	private taskTemplateTagList?: Promise<SelectListItemModel[]>;
	private taskflowTemplateTagList?: Promise<SelectListItemModel[]>;

	getTeamList() {
		// The teams are already loaded by the user service, but are provided like the other lists for consistency
		return this.teamList ??= Promise.resolve(this.userService.activeTeams.map(team => new SelectListItemModel(team.id!, team.name)));
	}

	getTaskAreaList() {
		return this.taskAreaList ??= this.taskService.getAllTaskAreas()
			.then(taskAreas => taskAreas.filter(taskArea => !taskArea.deleted).map(taskArea => new SelectListItemModel(taskArea.id!, taskArea.name)));
	}

	getReasonList() {
		return this.reasonList ??= this.reasonService.getAllReasons(false, true)
			.then(reasons => reasons.map(reason => new SelectListItemModel(reason.id!, reason.name)));
	}

	/** The saved queries of the given type, which a Query filter can reference. */
	async getSavedQueryList(queryType: QueryType) {
		this.savedQueryList ??= this.queryService.getQueries()
			.then(queries => queries.filter(query => query.data).map(query => ({ queryType: (JSON.parse(query.data) as DynamicQueryModel).queryType, item: new SelectListItemModel(query.id!, query.name) })));
		return (await this.savedQueryList).filter(savedQuery => savedQuery.queryType == queryType).map(savedQuery => savedQuery.item);
	}

	getProductPlanTagList() {
		return this.productPlanTagList ??= this.productPlanService.getAllProductPlanTags()
			.then(tags => tags.map(tag => new SelectListItemModel(tag, tag)));
	}

	getProductTagList() {
		return this.productTagList ??= this.productService.getAllProductTags()
			.then(tags => tags.map(tag => new SelectListItemModel(tag.tag, tag.tag)));
	}

	getTaskTemplateTagList() {
		return this.taskTemplateTagList ??= this.taskflowTemplateService.getAllTaskTemplateTags()
			.then(tags => tags.map(tag => new SelectListItemModel(tag.tag, tag.tag)));
	}

	getTaskflowTemplateTagList() {
		return this.taskflowTemplateTagList ??= this.taskflowTemplateService.getAllTaskflowTemplateTags()
			.then(tags => tags.map(tag => new SelectListItemModel(tag.tag, tag.tag)));
	}
}
